import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  crossesTranscriptDocumentBoundary,
  getPublicGoogleAnalyticsPageTitle,
  getPublicGoogleAnalyticsReferrer,
  GOOGLE_ANALYTICS_CONSENT_KEY,
  isAnalyticsConsentWithdrawal,
  isPublicGoogleAnalyticsPath,
  loadGoogleAnalytics,
  readGoogleAnalyticsConsent,
  saveGoogleAnalyticsConsent,
  sendPublicPageView,
  updateGoogleAnalyticsConsent,
  type GoogleAnalyticsConsentChoice,
} from "@/utils/googleAnalytics";

const measurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID?.trim() ?? "";

export function GoogleAnalyticsConsent() {
  const location = useLocation();
  const [consent, setConsent] = useState<GoogleAnalyticsConsentChoice>("unknown");
  const [isEditing, setIsEditing] = useState(false);
  const previousPublicPath = useRef<string | null>(null);
  const previousDocumentPath = useRef(location.pathname);
  const crossedPrivateRoute = useRef(false);
  const isPublicRoute = isPublicGoogleAnalyticsPath(location.pathname);

  useEffect(() => {
    setConsent(readGoogleAnalyticsConsent());
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== GOOGLE_ANALYTICS_CONSENT_KEY && event.key !== null) return;

      const nextConsent = readGoogleAnalyticsConsent();
      const withdrawingAcceptedConsent = isAnalyticsConsentWithdrawal(consent, nextConsent);
      setConsent(nextConsent);

      if (withdrawingAcceptedConsent) {
        updateGoogleAnalyticsConsent(false, measurementId);
        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [consent]);

  useEffect(() => {
    const crossedDocumentBoundary = crossesTranscriptDocumentBoundary(previousDocumentPath.current, location.pathname);
    previousDocumentPath.current = location.pathname;
    if (crossedDocumentBoundary) {
      updateGoogleAnalyticsConsent(false, measurementId);
      window.location.reload();
      return;
    }

    if (!isPublicRoute) {
      const leavingTrackedPublicRoute = previousPublicPath.current !== null;
      previousPublicPath.current = null;
      crossedPrivateRoute.current = true;

      if (leavingTrackedPublicRoute && document.getElementById("coachveera-google-analytics-tag")) {
        updateGoogleAnalyticsConsent(false, measurementId);
        window.location.reload();
      }

      return;
    }

    if (!measurementId || consent !== "accepted") return;

    const pageTitle = getPublicGoogleAnalyticsPageTitle(location.pathname);
    const pageReferrer = getPublicGoogleAnalyticsReferrer(previousPublicPath.current, crossedPrivateRoute.current) ?? "";
    loadGoogleAnalytics(measurementId, location.pathname, pageTitle, pageReferrer);
    sendPublicPageView(
      measurementId,
      location.pathname,
      pageTitle,
      pageReferrer,
    );
    previousPublicPath.current = location.pathname;
  }, [consent, isPublicRoute, location.pathname]);

  if (!measurementId || !isPublicRoute) return null;

  const saveChoice = (choice: Exclude<GoogleAnalyticsConsentChoice, "unknown">) => {
    const withdrawingAcceptedConsent = isAnalyticsConsentWithdrawal(consent, choice);
    saveGoogleAnalyticsConsent(choice);
    setIsEditing(false);

    if (withdrawingAcceptedConsent) {
      updateGoogleAnalyticsConsent(false, measurementId);
      window.location.reload();
      return;
    }

    setConsent(choice);
  };

  if (consent !== "unknown" && !isEditing) {
    return (
      <div className="fixed bottom-4 left-4 z-[100]">
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="rounded-full border border-border bg-background/95 px-4 py-2 text-sm font-medium text-foreground shadow-lg backdrop-blur focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open Google Analytics settings"
        >
          Analytics settings
        </button>
      </div>
    );
  }

  return (
    <section
      aria-label="Google Analytics consent"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-[100] mx-auto max-w-3xl rounded-xl border border-border bg-background p-4 text-foreground shadow-xl sm:p-5"
    >
      <p className="text-sm leading-6">
        May we use Google Analytics to understand visits to Coach Veera&apos;s public pages? GA4 loads only if you allow it.
        You can reject or change your choice later. See our <Link to="/privacy" className="underline underline-offset-4">Privacy Policy</Link>.
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => saveChoice("accepted")}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Allow Google Analytics
        </button>
        <button
          type="button"
          onClick={() => saveChoice("rejected")}
          className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Reject Google Analytics
        </button>
      </div>
    </section>
  );
}
